import { type ReactNode } from 'react';
import { Center, Column , CircularProgress } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { COMPACT_LIST_MAX_HEIGHT, LIST_MAX_HEIGHT } from './constants';
import { EmbeddedListItem } from './EmbeddedListItem';
import { type IListItemData } from './professionalAdapters';

interface ITabContentProps {
  isLoading: boolean;
  professionals: IListItemData[];
  selectedProfessionals: Set<string>;
  onSelectProfessionals: (ids: string[], forceAdd?: boolean) => void;
  onViewSkills: (pro: IListItemData) => void;
  emptyComponent: ReactNode;
  testId: string;
  /** Ids already invited to the shift — rendered as non-selectable "Sent" rows. */
  sentProfessionalIds?: Set<string>;
  /**
   * Renders the list at the shorter locked height so the "Review Shifts" CTA
   * clears the fold when the office has no valid payment method (GOTU-4421).
   */
  compact?: boolean;
}

const SCROLL_STYLE = {
  overflowY: 'auto' as const,
  scrollbarWidth: 'thin' as const,
  scrollbarColor: `${colors.neutral[200]} transparent`,
  padding: '0 8px',
};

export const TabContent = ({
  isLoading,
  professionals,
  selectedProfessionals,
  onSelectProfessionals,
  onViewSkills,
  emptyComponent,
  testId,
  sentProfessionalIds,
  compact = false,
}: ITabContentProps) => {
  const maxHeight = compact ? COMPACT_LIST_MAX_HEIGHT : LIST_MAX_HEIGHT;

  if (isLoading) {
    return (
      <Center height={`${maxHeight}px`}>
        <CircularProgress />
      </Center>
    );
  }

  if (professionals.length === 0) {
    return <>{emptyComponent}</>;
  }

  return (
    <Column
      data-testid={testId}
      data-cy={testId}
      style={{ maxHeight, ...SCROLL_STYLE }}
    >
      {professionals.map((pro) => (
        <EmbeddedListItem
          key={pro.id}
          id={pro.id}
          fullName={pro.fullName}
          isFavorite={pro.isFavorite}
          isAvailable={pro.isAvailable}
          isSelected={selectedProfessionals.has(pro.id)}
          profileImage={pro.profileImage}
          rating={pro.rating}
          badges={pro.badges}
          isSent={sentProfessionalIds?.has(pro.id)}
          onItemClick={(id) => onSelectProfessionals([id])}
          onViewSkills={() => onViewSkills(pro)}
        />
      ))}
    </Column>
  );
};
